import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ResourcePicker,
  type PickerPage,
  type PickerPasteResult,
  type PickerPasteSkip,
  type PickerQuery,
  type PickerSelection,
  type PickerSortOption,
  type ResourcePickerProps,
} from '@nucleus-commerce/admin-kit/resource-picker';
import type {
  ProductFilters,
  ProductSelection,
  ProductStatus,
  StoreProductStatus,
} from '@nucleus-commerce/api-client/admin';

import {
  resourcePickersApi,
  type OrgProductListParams,
  type StoreProductListParams,
} from '../api/resource-pickers-api';
import { resourcePickerKeys } from '../hooks/resource-picker-keys';
import { useProductFilterDefs } from '../hooks/use-product-filter-defs';
import { useProductRow, type OrgProductRow, type StoreProductRow } from '../hooks/use-product-row';
import { toProductListParams, type ProductPickerScope } from '../lib/product-filters';
import {
  MAX_SELECTION_PRODUCTS,
  toProductSelection,
  type ProductSelectionPins,
} from '../lib/product-selection';

import { FromCollectionSource } from './from-collection-source';
import { PICKER_PAGE_SIZE } from './product-filter-defs';

/** The row type of a scope: master rows in org scope, store rows (keyed by `productId`) in a store. */
export type ProductPickerRow<S extends ProductPickerScope = ProductPickerScope> = S extends {
  kind: 'store';
}
  ? StoreProductRow
  : OrgProductRow;

/** The statuses a scope lists: master statuses in org scope, store statuses in a store. */
export type ProductPickerStatus<S extends ProductPickerScope = ProductPickerScope> = S extends {
  kind: 'store';
}
  ? StoreProductStatus
  : ProductStatus;

/** The target the picked products go into; rows it already holds are locked (`memberOf`). */
export interface ProductMemberOf {
  kind: 'store' | 'collection' | 'schedule';
  id: string;
}

export interface ProductPickerProps<S extends ProductPickerScope = ProductPickerScope> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `{ kind: 'org' }`: the master catalog. `{ kind: 'store', storeId }`: one store's catalog. */
  scope: S;
  /** Guided-tour anchor for the dialog. */
  tour?: string;
  /** Default "Add products". */
  title?: string;
  /** Default "Add". */
  confirmLabel?: string;
  /** `true` (default) for any number, a number to cap, `false` for one product. */
  multiple?: boolean | number;
  /**
   * Offers "Select all N matching" (with `multiple: true` only); the result is then a query.
   * `true` uses the endpoints' limit of 5,000 products.
   */
  selectAllMatching?: boolean | { max: number };
  /** Text typed beside a Browse button; prefills the search. */
  initialQuery?: string;
  /** Ticked when the picker opens and can be unticked: the field's current value. */
  initialSelectedIds?: readonly string[];
  /**
   * The target the products are added to: the server flags rows it already holds and the
   * picker locks them ("Already added", "In another schedule"). With "Select all matching" and
   * a store target the selection also carries `notInStoreId`, so members never count.
   */
  memberOf?: ProductMemberOf;
  /** Ticked and locked as "Already added": rows the caller's list already holds. */
  lockedIds?: ReadonlySet<string>;
  /** What a locked row says instead of "Already added". */
  lockedLabel?: string;
  /** Never listed (the product being edited, say); a query selection excludes them too. */
  excludeIds?: ReadonlySet<string>;
  /** Only these statuses are listed, and the Status filter is not offered. */
  statuses?: ReadonlyArray<ProductPickerStatus<S>>;
  /**
   * Org scope: the store whose collections "From collection" opens. A store scope uses its own
   * store; an org picker without it offers no "From collection".
   */
  storeId?: string;
  /** Controls above the footer; a function gets the current selection. */
  footerContent?: ResourcePickerProps<ProductPickerRow<S>>['footerContent'];
  /**
   * The picker's selection, and the same as the `ProductSelection` the bulk endpoints take
   * (pinned statuses, the membership exclusion and `excludeIds` included).
   */
  onConfirm: (
    selection: PickerSelection<ProductPickerRow<S>>,
    productSelection: ProductSelection,
  ) => unknown;
}

type AnyRow = OrgProductRow | StoreProductRow;

/** Why a pasted value's product is left out (`resourcePickers:pasteSkipped.*`). */
type PasteSkipReason = 'alreadyAdded' | 'inAnotherSchedule' | 'notAvailable';

const SEARCH_BY = ['all', 'title', 'sku', 'barcode', 'id'] as const;
/** The lists take at most this many `ids` per call. */
const IDS_PER_CALL = 100;

const ORG_SORTS = ['title', '-title', '-createdAt', 'createdAt', '-updatedAt', 'price', '-price'];
const STORE_SORTS = [
  'title',
  '-title',
  '-createdAt',
  'createdAt',
  '-publishedAt',
  'price',
  '-price',
];

/**
 * The Shopify product picker over the master catalog or one store's catalog: search by title,
 * SKU, barcode or id; every product filter of the scope; sort; paste SKUs, handles or ids; "From
 * collection"; rows with thumbnail, price range, status and (store) available stock. With
 * `selectAllMatching` the selection can cover every matching product (a query selection).
 */
export function ProductPicker<S extends ProductPickerScope>(props: ProductPickerProps<S>) {
  return <ScopedProductPicker {...(props as unknown as ProductPickerProps)} />;
}

function ScopedProductPicker({
  open,
  onOpenChange,
  scope,
  tour,
  title,
  confirmLabel,
  multiple = true,
  selectAllMatching,
  initialQuery,
  initialSelectedIds,
  memberOf,
  lockedIds,
  lockedLabel,
  excludeIds,
  statuses,
  storeId: contextStoreId,
  footerContent,
  onConfirm,
}: ProductPickerProps) {
  const { t, i18n } = useTranslation(['resourcePickers', 'common']);
  const productRow = useProductRow();
  const number = new Intl.NumberFormat(i18n.language);
  const storeId = scope.kind === 'store' ? scope.storeId : null;
  const kind = scope.kind;
  const memberKey = memberOf ? `${memberOf.kind}:${memberOf.id}` : undefined;
  // A store membership is excluded by filter, so "Not in store" is taken.
  const excludesMembers = kind === 'org' && memberOf?.kind === 'store' ? memberOf.id : null;
  const filters = useProductFilterDefs(scope, {
    omit: [...(statuses ? ['status'] : []), ...(excludesMembers ? ['notInStoreId'] : [])],
  });
  const selectAll =
    selectAllMatching === true
      ? { max: MAX_SELECTION_PRODUCTS }
      : selectAllMatching === false
        ? undefined
        : selectAllMatching;
  const canSelectAll = selectAll !== undefined && multiple === true;
  const statusList = statuses ? [...statuses] : undefined;
  const statusKey = statusList?.join(',');
  const excluded = useMemo(() => (excludeIds ? [...excludeIds].sort() : []), [excludeIds]);

  /** What the caller fixed, in list params and in a selection's JSON filters. */
  const pinnedParams = useMemo(
    (): Record<string, string> => ({
      ...(statusKey ? { status: statusKey } : {}),
      ...(memberKey ? { memberOf: memberKey } : {}),
    }),
    [memberKey, statusKey],
  );
  const pins = useMemo((): ProductSelectionPins => {
    const pinnedFilters: ProductFilters = {
      ...(statusKey ? { status: statusKey.split(',') as ProductFilters['status'] } : {}),
      ...(excludesMembers ? { notInStoreId: excludesMembers } : {}),
    };
    return { filters: pinnedFilters, excludeIds: excluded };
  }, [excluded, excludesMembers, statusKey]);

  const idOf = (row: AnyRow) => ('productId' in row ? row.productId : row.id);
  const fetchPage = (params: Record<string, string | number | boolean>, signal: AbortSignal) =>
    storeId
      ? resourcePickersApi.storeProducts(storeId, params as StoreProductListParams, signal)
      : resourcePickersApi.products(params as OrgProductListParams, signal);

  const list = (query: PickerQuery) => {
    const params: Record<string, string | number> = {
      limit: PICKER_PAGE_SIZE,
      ...(query.q ? { q: query.q, ...(query.searchBy ? { searchBy: query.searchBy } : {}) } : {}),
      ...toProductListParams(query.filters, kind),
      ...(query.sort ? { sort: query.sort } : {}),
      ...pinnedParams,
    };
    return {
      queryKey: storeId
        ? resourcePickerKeys.storeProducts(storeId, {
            ...params,
            excluded,
            canSelectAll,
            excludesMembers,
          })
        : resourcePickerKeys.products({ ...params, excluded, canSelectAll, excludesMembers }),
      queryFn: async ({
        cursor,
        signal,
      }: {
        cursor: string | undefined;
        signal: AbortSignal;
      }): Promise<PickerPage<AnyRow>> => {
        // The total turns on "Select all N matching"; the API counts on the first page only.
        const first = cursor === undefined && canSelectAll;
        const [page, selectable] = await Promise.all([
          fetchPage(
            { ...params, ...(cursor ? { cursor } : {}), ...(first ? { includeTotal: true } : {}) },
            signal,
          ),
          // Members of a store target are excluded from a query selection by `notInStoreId`, so
          // "Select all N" offers (and the server's count checks) the rows not in the store yet.
          first && excludesMembers
            ? fetchPage(
                { ...params, notInStoreId: excludesMembers, limit: 1, includeTotal: true },
                signal,
              )
            : Promise.resolve(null),
        ]);
        const items: AnyRow[] =
          excluded.length > 0
            ? page.items.filter((row: AnyRow) => !excluded.includes(idOf(row)))
            : page.items;
        const counted = selectable ?? page;
        return {
          items,
          nextCursor: page.nextCursor,
          ...(counted.total !== undefined
            ? {
                total: counted.total,
                totalCapped: counted.totalCapped ?? false,
                // The store's members are locked rows the `notInStoreId` count leaves out.
                ...(selectable ? { totalOfSelectable: true } : {}),
              }
            : {}),
        };
      },
    };
  };

  /** Rows by id through the list's `ids` param: at most 100 a call, the calls in parallel. */
  const readByIds = async (
    ids: readonly string[],
    params: Record<string, string>,
    signal: AbortSignal,
  ): Promise<Map<string, AnyRow>> => {
    const chunks: string[][] = [];
    for (let start = 0; start < ids.length; start += IDS_PER_CALL) {
      chunks.push(ids.slice(start, start + IDS_PER_CALL));
    }
    const pages = await Promise.all(
      chunks.map((chunk) =>
        fetchPage({ ids: chunk.join(','), limit: chunk.length, ...params }, signal),
      ),
    );
    return new Map(
      pages.flatMap((page) => (page.items as AnyRow[]).map((row) => [idOf(row), row] as const)),
    );
  };

  const getByIds = async (ids: readonly string[], signal: AbortSignal): Promise<AnyRow[]> => {
    // Not limited to the pinned statuses: an initial id shows whatever its status.
    const byId = await readByIds(ids, memberKey ? { memberOf: memberKey } : {}, signal);
    return ids.flatMap((id) => byId.get(id) ?? []);
  };

  const onPaste = async (values: string[]): Promise<PickerPasteResult> => {
    const found = await resourcePickersApi.lookup(storeId, { values, by: 'auto' });
    const valuesOf = new Map<string, string[]>();
    for (const match of found.matches) {
      valuesOf.set(match.productId, [...(valuesOf.get(match.productId) ?? []), match.value]);
    }
    const matched = [...valuesOf.keys()];
    // The lookup knows neither the target nor the pinned statuses, so the matches are read again
    // through the list with them: a product outside the statuses does not come back, and one the
    // target already holds comes back as a member. Neither may be added (a store's members would
    // otherwise get their publication rewritten, and "all matching" unions `includeIds` on top
    // of the pinned filters).
    const pinned =
      matched.length > 0 && (statusKey || memberKey)
        ? await readByIds(matched, pinnedParams, new AbortController().signal)
        : null;
    const reasonOf = (id: string): PasteSkipReason | null => {
      if (excludeIds?.has(id)) return 'notAvailable';
      if (lockedIds?.has(id)) return 'alreadyAdded';
      if (!pinned) return null;
      const row = pinned.get(id);
      if (!row) return 'notAvailable';
      if (!row.isMember) return null;
      return row.memberReason === 'owned_elsewhere' ? 'inAnotherSchedule' : 'alreadyAdded';
    };
    const ids: string[] = [];
    const skipped: PickerPasteSkip[] = [];
    for (const id of matched) {
      const reason = reasonOf(id);
      if (reason === null) {
        ids.push(id);
        continue;
      }
      for (const value of valuesOf.get(id) ?? []) {
        skipped.push({ value, reason: t(`pasteSkipped.${reason}`) });
      }
    }
    return { ids, notFound: found.notFound, ...(skipped.length > 0 ? { skipped } : {}) };
  };

  const sortOptions: PickerSortOption[] = (kind === 'store' ? STORE_SORTS : ORG_SORTS).map(
    (value) => ({ value, label: t(`sort.${value}`) }),
  );
  const collectionStoreId = storeId ?? contextStoreId ?? null;

  return (
    <ResourcePicker<AnyRow>
      open={open}
      onOpenChange={onOpenChange}
      tour={tour}
      title={title ?? t(multiple === false ? 'products.titleOne' : 'products.title')}
      confirmLabel={confirmLabel ?? t('common:resourcePicker.add')}
      searchPlaceholder={t('products.search')}
      emptyTitle={t('products.empty')}
      countLabel={(count, max) =>
        max === undefined
          ? t('products.selected', { count, number: number.format(count) })
          : t('products.selectedOf', {
              count,
              number: number.format(count),
              max: number.format(max),
            })
      }
      multiple={multiple}
      {...(initialQuery !== undefined ? { initialQuery } : {})}
      {...(initialSelectedIds ? { initialSelectedIds } : {})}
      list={list}
      getId={idOf}
      getRow={(row) => {
        const options = { locked: lockedIds?.has(idOf(row)) ?? false, lockedLabel };
        return 'productId' in row ? productRow.store(row, options) : productRow.org(row, options);
      }}
      filters={filters}
      searchBy={{
        options: SEARCH_BY.map((value) => ({ value, label: t(`searchBy.${value}`) })),
        defaultValue: 'all',
      }}
      sortOptions={sortOptions}
      defaultSort="title"
      {...(selectAll ? { selectAllMatching: selectAll } : {})}
      getByIds={getByIds}
      onPaste={onPaste}
      {...(collectionStoreId && multiple !== false
        ? {
            sources: (api) => (
              <FromCollectionSource
                api={api}
                storeId={collectionStoreId}
                selectAll={canSelectAll ? selectAll.max : null}
              />
            ),
          }
        : {})}
      footerContent={footerContent}
      onConfirm={(selection) => onConfirm(selection, toProductSelection(selection, kind, pins))}
    />
  );
}
